import ErrorOverlay from 'components/ErrorOverlay'
import FileCard from 'components/FileCard'
import ScrollListener from 'components/ScrollListener'
import { ALL_FILTER, FileFolder, INTERACTION_EVENT_OPEN, InteractionTrackingSource, MyFile } from 'interfaces'
import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router'
import { setComposerFile } from 'services/compose'
import { addContentToSelection, removeContentFromSelection, setVisibleSelectableItems } from 'services/content'
import { addRecentSource, trackFileFolderInteraction } from 'services/content/recommended/actions'
import { selectedContentItemsSelector } from 'services/content/selectors'
import { message } from 'services/snackbar'
import { setSorting } from 'services/uploads'
import { deleteFiles, setFileInPreview } from 'services/uploads/actions'
import { librarySortingSelector, myFileFoldersArraySelector } from 'services/uploads/selectors'
import { libraryFilesToCSV } from 'services/uploads/util'
import { arrayToRecord, ContentType } from 'shared'
import { downloadCSV } from 'shared/utils/file/csv'
import StoreState, { StoreThunkDispatch } from 'store/state'
import FileFolderViewHeader from '../../components/FileFolderViewHeader'
import styles from './MyUploadsFolderView.pcss'

const SCROLL_THRESHOLD = 350
const PAGE_SIZE = 40

interface MyUploadsFolderViewOwnProps {
  backUrl?: string
}

interface ConnectedMyUploadsFolderViewProps {
  folders: FileFolder[]
  createPostFromFile: (url: string, name?: string) => void
  setFileInPreview: (file: MyFile) => void
  trackOpen: (id: string) => Promise<any>
  addRecentSource: (source: InteractionTrackingSource) => void
  onDeleteFiles: (files: MyFile[], force?: boolean) => Promise<any>
}

type Selection = { active: boolean, files: Record<string, boolean>, allSelected: boolean }

export type MyUploadsFolderViewProps = MyUploadsFolderViewOwnProps & ConnectedMyUploadsFolderViewProps

export function MyUploadsFolderView(props: MyUploadsFolderViewProps) {
  const intl = useIntl()
  const dispatch = useDispatch()
  const selectedItems = useSelector(selectedContentItemsSelector)
  const sorting = useSelector(librarySortingSelector)
  const location = useLocation()
  const navigate = useNavigate()
  const params = useParams()

  const [filter, setFilter] = useState(ALL_FILTER)
  const folder = props.folders.find(f => f.id.toString() === params.folderId)
  const folderSorting = folder ? sorting[folder.id] : null
  const [page, setPage] = useState(1)
  const visibleFilesEndIndex = page * PAGE_SIZE

  useEffect(() => {
    return () => {
      dispatch(setVisibleSelectableItems({}))
    }
  }, [dispatch])

  // Record "open" event for the active folder
  useEffect(() => {
    if (folder) {
      props.trackOpen(folder.id)
      props.addRecentSource(folder)
    }
    // EXPL: record the open event once per folder navigation (params.folderId). `folder` and the
    // tracking callbacks are read at that moment — keying on `folder` would re-fire the open event
    // every time the folder's data refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.folderId])

  const visibleFiles = useMemo(() => {
    const filtered = folder
      ? filter === ALL_FILTER
        ? Object.values(folder.files)
        : Object.values(folder.files).filter(f => f.type === filter)
      : []

    const sorted = filtered.sort((f1: MyFile, f2: MyFile) => {
      // sort files based on sortBy and sortDirection
      if (folderSorting?.sortBy === 'name') {
        return folderSorting?.sortDirection === 'asc'
          ? f2.name.localeCompare(f1.name)
          : f1.name.localeCompare(f2.name)
      }
      return folderSorting?.sortDirection === 'asc'
        ? f1.createdAt.localeCompare(f2.createdAt)
        : f2.createdAt.localeCompare(f1.createdAt)
    })
    return sorted
  }, [filter, folder, folderSorting?.sortBy, folderSorting?.sortDirection])

  const hasSelectedItems = visibleFiles.some(f => selectedItems[f.id])

  const allSelected = useMemo(() => {
    return visibleFiles.length > 0 && visibleFiles.every(f => selectedItems[f.id])
  }, [visibleFiles, selectedItems])

  useEffect(() => {
    dispatch(setVisibleSelectableItems(arrayToRecord(visibleFiles, 'id')))
  }, [dispatch, visibleFiles])

  useEffect(() => {
    if (!folder) {
      return
    }

    const defaultFilter = folder.filters.length !== 1 ? ALL_FILTER : folder.filters[0]

    if (filter !== defaultFilter && !folder.filters.includes(filter as ContentType)) {
      setFilter(defaultFilter)
    }
  }, [folder, filter])

  if (!folder) {
    return (
      <ErrorOverlay
        error={intl.formatMessage({ id: 'uploads.error.folder-not-found-title' })}
        subtitle={intl.formatMessage({ id: 'uploads.error.folder-not-found-subtitle' })}
        originalError="folder not found"
        className={styles['error-box']}
      />
    )
  }

  const loadMore = () => {
    if (page * PAGE_SIZE < visibleFiles.length) {
      setPage(page + 1)
    }
  }

  const createPostFromFile = (file: MyFile) => {
    props.createPostFromFile(file.url, file.name)
    navigate('/composer', { state: { floating: true, modal: 'compose', opener: location.pathname, background: location } })
  }

  const onSortChange = (sortBy: 'name' | 'date', sortDirection: 'asc' | 'desc') => {
    dispatch(setSorting({ folderId: folder.id, sortBy, sortDirection }))
  }

  const onFilterChange = (f: string) => {
    setFilter(f)
  }

  const toggleAllSelected = () => {
    const ids = visibleFiles.map(f => f.id)
    if (allSelected) {
      dispatch(removeContentFromSelection(ids))
    } else {
      dispatch(addContentToSelection(visibleFiles))
    }
  }

  const onFileClick = (file: MyFile) => {
    props.setFileInPreview(file)
  }

  const deleteSelectedFiles = () => {
    const selectedFiles = visibleFiles.filter(f => selectedItems[f.id])
    props.onDeleteFiles(selectedFiles).catch(e => {
      const invalidFiles = e.invalidFiles || []
      const reasons = new Set(invalidFiles.map((f: any) => f.reason))
      if (reasons.size > 0) {
        dispatch(message('Some of the files couldn\'t be deleted: ' + [...reasons].join(' '), 'error'))
      } else {
        dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
      }
    })
    dispatch(removeContentFromSelection(selectedFiles.map(f => f.id)))
  }

  const downloadSelectedFilesUrls = () => {
    const entries = visibleFiles.filter(f => selectedItems[f.id]).map(file => ({ folderName: folder.name, file }))
    downloadCSV(libraryFilesToCSV(entries), 'links-download.csv')
  }

  const scrollEl = document.getElementsByTagName('main')[0]

  return (
    <ScrollListener
      scrollElement={scrollEl}
      emitInitial
      emitTreshold={SCROLL_THRESHOLD}
      onScroll={loadMore}
    >
      <div>
        <FileFolderViewHeader
          folderId={folder.id}
          folderName={folder.name}
          activeFilter={filter}
          filters={folder.filters}
          backUrl={props.backUrl}
          sorting={{
            sortBy: folderSorting?.sortBy || 'date',
            sortDirection: folderSorting?.sortDirection || 'desc',
            onChange: onSortChange
          }}
          bulkActions={{
            allSelected,
            disabled: !hasSelectedItems,
            onDelete: deleteSelectedFiles,
            onDownload: downloadSelectedFilesUrls,
            onToggleAllSelected: toggleAllSelected
          }}
          actionsDisabled={visibleFiles.length === 0}
          onFilterChange={onFilterChange}
        />
        <section className={styles.grid}>
          {
            visibleFiles.length > 0 ? visibleFiles.slice(0, visibleFilesEndIndex).map(file => (
              <FileCard
                small
                file={file}
                key={file.key}
                selected={Boolean(selectedItems[file.id])}
                className={styles.file}
                checkboxClassName={styles['file-checkbox']}
                onClick={onFileClick}
                onCreatePost={createPostFromFile}
              />
            )) : (
              <ErrorOverlay
                error={intl.formatMessage({ id: `uploads.error.carousel-no-${filter}` })}
                subtitle=""
                originalError="no uploads"
                className={`${styles['error-box']} ${styles['error-grid']}`}
              />
            )
          }
        </section>
      </div>
    </ScrollListener>
  )
}

function mapStateToProps(state: StoreState) {
  return {
    folders: myFileFoldersArraySelector(state)
  }
}

function mapDispatchToProps(dispatch: StoreThunkDispatch) {
  return {
    createPostFromFile: (url: string, name?: string) => dispatch(setComposerFile({ url, name })),
    setFileInPreview: (file: MyFile) => dispatch(setFileInPreview(file)),
    trackOpen: (id: string) => dispatch(trackFileFolderInteraction({ folderIds: [id], event: INTERACTION_EVENT_OPEN })).unwrap(),
    addRecentSource: (source: InteractionTrackingSource) => dispatch(addRecentSource(source)),
    onDeleteFiles: (files: MyFile[], force?: boolean) => dispatch(deleteFiles({ files, force })).unwrap()
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MyUploadsFolderView)
